/**
 * Dedicated inspector for the `image` node (ADR-0069) — the "sezioni +
 * anteprima live" authoring surface the owner chose.
 *
 * The generic schema-driven field loop in NodeEditorDrawer can't render
 * the image node's NESTED config (resize / crop / rotate / adjust /
 * output sub-objects + the overlays discriminated-union list) — they
 * degrade to empty opaque "black box" inputs (the owner's screenshot
 * complaint: "sono tutti input liberi"). This component replaces that
 * with real per-section controls (select / number / colour / checkbox)
 * PLUS a live preview: upload a sample image, tweak a section, see the
 * rendered result — driven by `POST /workflows/preview-image`, which
 * shares the exact node pipeline (so no drift between preview and run).
 *
 * The sections map to the backend's fixed-order pipeline:
 *   source → crop → resize(fit/pad) → rotate/flip → adjust → overlays → output
 *
 * The nullable sections (resize/crop/rotate/adjust) render behind an
 * enable toggle — off = the section is `null` (a no-op), matching the
 * `Section | None` Pydantic shape.
 */
import { ImagePlus, Plus, Trash2 } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { ExpressionField } from "@/components/workflows/ExpressionField";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { workflowsApi, type PreviewImageResponse } from "@/lib/api-workflows";

// ---- config shapes (mirror hub.workflow.catalog ImageConfig) ---------------

type SourceType = "binary" | "base64" | "dataurl" | "url";
type Fit = "contain" | "cover" | "fill" | "inside" | "outside";
type Gravity = "center" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w" | "nw";
type Flip = "none" | "horizontal" | "vertical";
type Format = "jpeg" | "png" | "webp";

interface ResizeSection {
  width?: number | null;
  height?: number | null;
  fit: Fit;
  gravity: Gravity;
}
interface CropSection {
  left: number;
  top: number;
  width: number;
  height: number;
}
interface RotateSection {
  degrees: number;
  expand: boolean;
  flip: Flip;
}
interface AdjustSection {
  grayscale: boolean;
  brightness: number;
  contrast: number;
  blur: number;
  sharpen: boolean;
}
interface TextOverlay {
  kind: "text";
  content: string;
  font_size: number;
  color: string;
  gravity: Gravity;
  offset_x: number;
  offset_y: number;
}
interface ImageOverlay {
  kind: "image";
  source_type: SourceType;
  source_value: string;
  scale: number;
  opacity: number;
  gravity: Gravity;
  offset_x: number;
  offset_y: number;
}
type Overlay = TextOverlay | ImageOverlay;
interface OutputSection {
  format: Format;
  quality: number;
  binary: string;
  file_name?: string | null;
}
export interface ImageConfig {
  source_type: SourceType;
  source_value: string;
  /** The single background under everything; null = transparent. */
  background?: string | null;
  resize?: ResizeSection | null;
  crop?: CropSection | null;
  rotate?: RotateSection | null;
  adjust?: AdjustSection | null;
  overlays: Overlay[];
  output: OutputSection;
  emit_info: boolean;
}

/** The image-config field names the generic loop must skip (this editor owns them). */
export const IMAGE_CONFIG_FIELDS = [
  "source_type",
  "source_value",
  "resize",
  "crop",
  "rotate",
  "adjust",
  "overlays",
  "output",
  "emit_info",
];

const GRAVITIES: Gravity[] = ["center", "n", "ne", "e", "se", "s", "sw", "w", "nw"];
const GRAVITY_LABEL: Record<Gravity, string> = {
  center: "Centro",
  n: "Alto",
  ne: "Alto-dx",
  e: "Destra",
  se: "Basso-dx",
  s: "Basso",
  sw: "Basso-sx",
  w: "Sinistra",
  nw: "Alto-sx",
};

const DEFAULT_RESIZE: ResizeSection = {
  width: 1024,
  height: 1024,
  fit: "contain",
  gravity: "center",
};
const DEFAULT_CROP: CropSection = { left: 0, top: 0, width: 512, height: 512 };
const DEFAULT_ROTATE: RotateSection = {
  degrees: 0,
  expand: true,
  flip: "none",
};
const DEFAULT_ADJUST: AdjustSection = {
  grayscale: false,
  brightness: 1,
  contrast: 1,
  blur: 0,
  sharpen: false,
};
const DEFAULT_TEXT_OVERLAY: TextOverlay = {
  kind: "text",
  content: "",
  font_size: 24,
  color: "#000000",
  gravity: "sw",
  offset_x: 8,
  offset_y: 8,
};
const DEFAULT_IMAGE_OVERLAY: ImageOverlay = {
  kind: "image",
  source_type: "binary",
  source_value: "logo",
  scale: 1,
  opacity: 1,
  gravity: "se",
  offset_x: 8,
  offset_y: 8,
};

// ---- small styled primitives ----------------------------------------------

const selectCls =
  "h-9 w-full rounded-md border border-border bg-background px-2 text-sm " +
  "focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50";

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label htmlFor={htmlFor} className="text-xs text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

function Section({
  title,
  hint,
  enabled,
  onToggle,
  children,
}: {
  title: string;
  hint?: string;
  /** When defined, the section is nullable and renders an enable toggle. */
  enabled?: boolean;
  onToggle?: (on: boolean) => void;
  children?: React.ReactNode;
}) {
  const collapsible = enabled !== undefined;
  return (
    <div className="rounded-lg border border-border/60 bg-surface/40 p-3">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-semibold">{title}</h4>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </div>
        {collapsible && onToggle && (
          <Switch checked={!!enabled} onCheckedChange={onToggle} aria-label={`Abilita ${title}`} />
        )}
      </div>
      {(!collapsible || enabled) && children && <div className="mt-3 space-y-3">{children}</div>}
    </div>
  );
}

function ColorInput({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#ffffff"}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="h-9 w-10 shrink-0 cursor-pointer rounded border border-border bg-background p-0.5"
        aria-label="Colore"
      />
      <Input value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} />
    </div>
  );
}

function NumberInput({
  value,
  onChange,
  disabled,
  step,
  min,
  placeholder,
}: {
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  disabled?: boolean;
  step?: number;
  min?: number;
  placeholder?: string;
}) {
  return (
    <Input
      type="number"
      value={value ?? ""}
      step={step}
      min={min}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => {
        const raw = e.target.value;
        onChange(raw === "" ? null : Number(raw));
      }}
    />
  );
}

function GravitySelect({
  value,
  onChange,
  disabled,
}: {
  value: Gravity;
  onChange: (g: Gravity) => void;
  disabled?: boolean;
}) {
  return (
    <select
      className={selectCls}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as Gravity)}
    >
      {GRAVITIES.map((g) => (
        <option key={g} value={g}>
          {GRAVITY_LABEL[g]}
        </option>
      ))}
    </select>
  );
}

// ---- component -------------------------------------------------------------

export interface ImageNodeEditorProps {
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  readOnly?: boolean;
  /** Ambient `.d.ts` for the ƒx templated fields (source_value / file_name / text). */
  jsAmbientTypes?: string;
}

export function ImageNodeEditor({
  config,
  onChange,
  readOnly = false,
  jsAmbientTypes,
}: ImageNodeEditorProps) {
  // Read the config with defaults so a freshly-dropped node (empty config)
  // renders sensible controls.
  const cfg: ImageConfig = React.useMemo(
    () => ({
      source_type: (config.source_type as SourceType) ?? "binary",
      source_value: (config.source_value as string) ?? "data",
      background: config.background === undefined ? "#ffffff" : (config.background as string | null),
      resize: (config.resize as ResizeSection | null) ?? null,
      crop: (config.crop as CropSection | null) ?? null,
      rotate: (config.rotate as RotateSection | null) ?? null,
      adjust: (config.adjust as AdjustSection | null) ?? null,
      overlays: (config.overlays as Overlay[]) ?? [],
      output: (config.output as OutputSection) ?? {
        format: "jpeg",
        quality: 85,
        binary: "data",
        file_name: null,
      },
      emit_info: config.emit_info === undefined ? true : !!config.emit_info,
    }),
    [config],
  );

  const patch = React.useCallback(
    (next: Partial<ImageConfig>) => {
      if (readOnly) return;
      onChange({ ...config, ...next });
    },
    [config, onChange, readOnly],
  );

  // ---- live preview --------------------------------------------------------
  const [sampleImage, setSampleImage] = React.useState<string | null>(null);
  const [sampleName, setSampleName] = React.useState<string | null>(null);
  const [preview, setPreview] = React.useState<PreviewImageResponse | null>(null);
  const [previewing, setPreviewing] = React.useState(false);
  const fileRef = React.useRef<HTMLInputElement>(null);

  const onPickSample = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setSampleImage(String(reader.result)); // a data: URL — the route decodes it
      setSampleName(file.name);
    };
    reader.readAsDataURL(file);
  };

  // Debounced preview whenever the config or the sample changes.
  React.useEffect(() => {
    if (!sampleImage) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    setPreviewing(true);
    const handle = window.setTimeout(async () => {
      try {
        const res = await workflowsApi.previewImage({
          config: config as Record<string, unknown>,
          sample_image: sampleImage,
        });
        if (!cancelled) setPreview(res);
      } catch {
        if (!cancelled)
          setPreview({ ok: false, data_url: null, width: null, height: null, error: "preview_failed" });
      } finally {
        if (!cancelled) setPreviewing(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [config, sampleImage]);

  const resize = cfg.resize;
  const crop = cfg.crop;
  const rotate = cfg.rotate;
  const adjust = cfg.adjust;

  return (
    <div className="mb-4 grid gap-4 border-b border-border/60 pb-4 lg:grid-cols-[minmax(0,1fr)_260px]">
      {/* ---- sections ---- */}
      <div className="space-y-3">
        {/* Sorgente */}
        <Section title="Sorgente" hint="Da dove arriva l'immagine di input.">
          <Field label="Tipo sorgente">
            <select
              className={selectCls}
              value={cfg.source_type}
              disabled={readOnly}
              onChange={(e) => patch({ source_type: e.target.value as SourceType })}
            >
              <option value="binary">Slot binario dell'item</option>
              <option value="base64">Base64</option>
              <option value="dataurl">Data URL</option>
              <option value="url">URL remoto (fetch)</option>
            </select>
          </Field>
          <Field label={cfg.source_type === "binary" ? "Nome dello slot" : "Valore"}>
            <ExpressionField
              value={cfg.source_value}
              onChange={(v) => patch({ source_value: v })}
              jsAmbientTypes={jsAmbientTypes}
              placeholder={cfg.source_type === "binary" ? "data" : "{{ $json.image_url }}"}
            />
          </Field>
        </Section>

        {/* Sfondo — the ONE canvas under everything */}
        <Section
          title="Sfondo"
          hint="Il colore sotto l'immagine: riempie il padding (Contain), gli angoli della rotazione e la trasparenza del PNG."
        >
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={cfg.background === null}
                disabled={readOnly}
                onCheckedChange={(on) => patch({ background: on ? null : "#ffffff" })}
                aria-label="Sfondo trasparente"
              />
              Trasparente
            </label>
            {cfg.background !== null && (
              <div className="flex-1">
                <ColorInput
                  value={cfg.background ?? "#ffffff"}
                  disabled={readOnly}
                  onChange={(v) => patch({ background: v })}
                />
              </div>
            )}
          </div>
          {cfg.background === null && (
            <p className="text-[11px] text-muted-foreground">
              Trasparente resta solo con output PNG/WebP; in JPEG viene comunque appiattito su
              bianco (JPEG non ha trasparenza).
            </p>
          )}
        </Section>

        {/* Dimensioni & Tela — resize */}
        <Section
          title="Ridimensiona & Tela"
          hint="Adatta e riempi su una tela (letterbox)."
          enabled={!!resize}
          onToggle={(on) => patch({ resize: on ? DEFAULT_RESIZE : null })}
        >
          {resize && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Larghezza (px)">
                  <NumberInput
                    value={resize.width}
                    min={1}
                    placeholder="auto"
                    disabled={readOnly}
                    onChange={(v) => patch({ resize: { ...resize, width: v } })}
                  />
                </Field>
                <Field label="Altezza (px)">
                  <NumberInput
                    value={resize.height}
                    min={1}
                    placeholder="auto"
                    disabled={readOnly}
                    onChange={(v) => patch({ resize: { ...resize, height: v } })}
                  />
                </Field>
              </div>
              <Field label="Adattamento">
                <select
                  className={selectCls}
                  value={resize.fit}
                  disabled={readOnly}
                  onChange={(e) => patch({ resize: { ...resize, fit: e.target.value as Fit } })}
                >
                  <option value="contain">Contain — adatta + riempi (letterbox)</option>
                  <option value="cover">Cover — riempi + ritaglia</option>
                  <option value="fill">Fill — stira</option>
                  <option value="inside">Inside — adatta senza padding</option>
                  <option value="outside">Outside — copri senza ritaglio</option>
                </select>
              </Field>
              <Field label="Ancoraggio">
                <GravitySelect
                  value={resize.gravity}
                  disabled={readOnly}
                  onChange={(g) => patch({ resize: { ...resize, gravity: g } })}
                />
              </Field>
              <p className="text-[11px] text-muted-foreground">
                Con Contain lo spazio vuoto viene riempito dallo <b>Sfondo</b> (sopra).
              </p>
            </>
          )}
        </Section>

        {/* Ritaglio — crop */}
        <Section
          title="Ritaglio"
          hint="Taglia una regione prima del resize."
          enabled={!!crop}
          onToggle={(on) => patch({ crop: on ? DEFAULT_CROP : null })}
        >
          {crop && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Sinistra (px)">
                <NumberInput
                  value={crop.left}
                  min={0}
                  disabled={readOnly}
                  onChange={(v) => patch({ crop: { ...crop, left: v ?? 0 } })}
                />
              </Field>
              <Field label="Alto (px)">
                <NumberInput
                  value={crop.top}
                  min={0}
                  disabled={readOnly}
                  onChange={(v) => patch({ crop: { ...crop, top: v ?? 0 } })}
                />
              </Field>
              <Field label="Larghezza (px)">
                <NumberInput
                  value={crop.width}
                  min={1}
                  disabled={readOnly}
                  onChange={(v) => patch({ crop: { ...crop, width: v ?? 1 } })}
                />
              </Field>
              <Field label="Altezza (px)">
                <NumberInput
                  value={crop.height}
                  min={1}
                  disabled={readOnly}
                  onChange={(v) => patch({ crop: { ...crop, height: v ?? 1 } })}
                />
              </Field>
            </div>
          )}
        </Section>

        {/* Rotazione — rotate */}
        <Section
          title="Rotazione & Specchio"
          enabled={!!rotate}
          onToggle={(on) => patch({ rotate: on ? DEFAULT_ROTATE : null })}
        >
          {rotate && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Gradi (orario)">
                  <NumberInput
                    value={rotate.degrees}
                    step={1}
                    disabled={readOnly}
                    onChange={(v) => patch({ rotate: { ...rotate, degrees: v ?? 0 } })}
                  />
                </Field>
                <Field label="Specchio">
                  <select
                    className={selectCls}
                    value={rotate.flip}
                    disabled={readOnly}
                    onChange={(e) =>
                      patch({ rotate: { ...rotate, flip: e.target.value as Flip } })
                    }
                  >
                    <option value="none">Nessuno</option>
                    <option value="horizontal">Orizzontale</option>
                    <option value="vertical">Verticale</option>
                  </select>
                </Field>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={rotate.expand}
                  disabled={readOnly}
                  onCheckedChange={(on) => patch({ rotate: { ...rotate, expand: on } })}
                  aria-label="Espandi la tela"
                />
                <span className="text-xs text-muted-foreground">
                  Espandi la tela (gli angoli scoperti usano lo <b>Sfondo</b>)
                </span>
              </div>
            </>
          )}
        </Section>

        {/* Colore & Filtri — adjust */}
        <Section
          title="Colore & Filtri"
          enabled={!!adjust}
          onToggle={(on) => patch({ adjust: on ? DEFAULT_ADJUST : null })}
        >
          {adjust && (
            <>
              <div className="flex gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={adjust.grayscale}
                    disabled={readOnly}
                    onCheckedChange={(on) => patch({ adjust: { ...adjust, grayscale: on } })}
                  />
                  Scala di grigi
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch
                    checked={adjust.sharpen}
                    disabled={readOnly}
                    onCheckedChange={(on) => patch({ adjust: { ...adjust, sharpen: on } })}
                  />
                  Nitidezza
                </label>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Luminosità">
                  <NumberInput
                    value={adjust.brightness}
                    step={0.1}
                    min={0}
                    disabled={readOnly}
                    onChange={(v) => patch({ adjust: { ...adjust, brightness: v ?? 1 } })}
                  />
                </Field>
                <Field label="Contrasto">
                  <NumberInput
                    value={adjust.contrast}
                    step={0.1}
                    min={0}
                    disabled={readOnly}
                    onChange={(v) => patch({ adjust: { ...adjust, contrast: v ?? 1 } })}
                  />
                </Field>
                <Field label="Sfocatura (px)">
                  <NumberInput
                    value={adjust.blur}
                    step={0.5}
                    min={0}
                    disabled={readOnly}
                    onChange={(v) => patch({ adjust: { ...adjust, blur: v ?? 0 } })}
                  />
                </Field>
              </div>
            </>
          )}
        </Section>

        {/* Sovrapposizioni — overlays */}
        <Section title="Sovrapposizioni" hint="Filigrane testo / logo, in ordine.">
          <div className="space-y-3">
            {cfg.overlays.map((ov, i) => (
              <div key={i} className="rounded-md border border-border/60 p-2.5">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-medium">
                    {ov.kind === "text" ? "Testo" : "Immagine"} #{i + 1}
                  </span>
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        patch({ overlays: cfg.overlays.filter((_, j) => j !== i) })
                      }
                      aria-label="Rimuovi sovrapposizione"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <OverlayFields
                  overlay={ov}
                  readOnly={readOnly}
                  jsAmbientTypes={jsAmbientTypes}
                  onChange={(next) =>
                    patch({ overlays: cfg.overlays.map((o, j) => (j === i ? next : o)) })
                  }
                />
              </div>
            ))}
            {!readOnly && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => patch({ overlays: [...cfg.overlays, { ...DEFAULT_TEXT_OVERLAY }] })}
                >
                  <Plus className="mr-1 h-4 w-4" /> Testo
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    patch({ overlays: [...cfg.overlays, { ...DEFAULT_IMAGE_OVERLAY }] })
                  }
                >
                  <Plus className="mr-1 h-4 w-4" /> Immagine
                </Button>
              </div>
            )}
          </div>
        </Section>

        {/* Output */}
        <Section title="Output" hint="Codec + dove finisce l'immagine.">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Formato">
              <select
                className={selectCls}
                value={cfg.output.format}
                disabled={readOnly}
                onChange={(e) =>
                  patch({ output: { ...cfg.output, format: e.target.value as Format } })
                }
              >
                <option value="jpeg">JPEG</option>
                <option value="png">PNG</option>
                <option value="webp">WebP</option>
              </select>
            </Field>
            <Field label="Qualità (jpeg/webp)">
              <NumberInput
                value={cfg.output.quality}
                min={1}
                disabled={readOnly || cfg.output.format === "png"}
                onChange={(v) => patch({ output: { ...cfg.output, quality: v ?? 85 } })}
              />
            </Field>
          </div>
          <Field label="Slot binario di destinazione">
            <Input
              value={cfg.output.binary}
              disabled={readOnly}
              onChange={(e) => patch({ output: { ...cfg.output, binary: e.target.value } })}
            />
          </Field>
          <Field label="Nome file (opzionale)">
            <ExpressionField
              value={cfg.output.file_name ?? ""}
              onChange={(v) => patch({ output: { ...cfg.output, file_name: v || null } })}
              jsAmbientTypes={jsAmbientTypes}
              placeholder="{{ $json.cd_ar }}.jpg"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <Switch
              checked={cfg.emit_info}
              disabled={readOnly}
              onCheckedChange={(on) => patch({ emit_info: on })}
            />
            Scrivi dimensioni/formato/byte in <code className="text-xs">$json.image</code>
          </label>
        </Section>
      </div>

      {/* ---- live preview pane ---- */}
      <div className="space-y-2 lg:sticky lg:top-2 lg:self-start">
        <div className="text-xs font-semibold text-muted-foreground">Anteprima</div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onPickSample(e.target.files?.[0])}
        />
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => fileRef.current?.click()}
        >
          <ImagePlus className="mr-1 h-4 w-4" />
          {sampleName ? "Cambia immagine" : "Immagine di prova"}
        </Button>
        {sampleName && (
          <div className="truncate text-[11px] text-muted-foreground" title={sampleName}>
            {sampleName}
          </div>
        )}
        <div className="flex min-h-[160px] items-center justify-center overflow-hidden rounded-md border border-border/60 bg-[repeating-conic-gradient(theme(colors.border)_0%_25%,transparent_0%_50%)] bg-[length:16px_16px] p-2">
          {!sampleImage ? (
            <span className="px-3 text-center text-xs text-muted-foreground">
              Carica un'immagine per vedere il risultato dal vivo.
            </span>
          ) : previewing ? (
            <span className="text-xs text-muted-foreground">Rendering…</span>
          ) : preview?.ok && preview.data_url ? (
            <img
              src={preview.data_url}
              alt="Anteprima"
              className="max-h-[280px] max-w-full object-contain"
            />
          ) : (
            <span className="px-3 text-center text-xs text-danger">
              {preview?.error ?? "Anteprima non disponibile"}
            </span>
          )}
        </div>
        {preview?.ok && preview.width && preview.height && (
          <div className="text-center text-[11px] text-muted-foreground">
            {preview.width} × {preview.height} px
          </div>
        )}
      </div>
    </div>
  );
}

// ---- overlay sub-form ------------------------------------------------------

function OverlayFields({
  overlay,
  onChange,
  readOnly,
  jsAmbientTypes,
}: {
  overlay: Overlay;
  onChange: (next: Overlay) => void;
  readOnly?: boolean;
  jsAmbientTypes?: string;
}) {
  if (overlay.kind === "text") {
    const ov = overlay;
    return (
      <div className="space-y-2">
        <Field label="Testo">
          <ExpressionField
            value={ov.content}
            onChange={(v) => onChange({ ...ov, content: v })}
            jsAmbientTypes={jsAmbientTypes}
            placeholder="{{ $json.label }}"
          />
        </Field>
        <div className="grid grid-cols-3 gap-2">
          <Field label="Dimensione">
            <NumberInput
              value={ov.font_size}
              min={1}
              disabled={readOnly}
              onChange={(v) => onChange({ ...ov, font_size: v ?? 24 })}
            />
          </Field>
          <Field label="Colore">
            <ColorInput
              value={ov.color}
              disabled={readOnly}
              onChange={(v) => onChange({ ...ov, color: v })}
            />
          </Field>
          <Field label="Ancoraggio">
            <GravitySelect
              value={ov.gravity}
              disabled={readOnly}
              onChange={(g) => onChange({ ...ov, gravity: g })}
            />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Offset X">
            <NumberInput
              value={ov.offset_x}
              disabled={readOnly}
              onChange={(v) => onChange({ ...ov, offset_x: v ?? 0 })}
            />
          </Field>
          <Field label="Offset Y">
            <NumberInput
              value={ov.offset_y}
              disabled={readOnly}
              onChange={(v) => onChange({ ...ov, offset_y: v ?? 0 })}
            />
          </Field>
        </div>
      </div>
    );
  }
  const ov = overlay;
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <Field label="Tipo sorgente">
          <select
            className={selectCls}
            value={ov.source_type}
            disabled={readOnly}
            onChange={(e) => onChange({ ...ov, source_type: e.target.value as SourceType })}
          >
            <option value="binary">Slot binario</option>
            <option value="base64">Base64</option>
            <option value="dataurl">Data URL</option>
            <option value="url">URL remoto</option>
          </select>
        </Field>
        <Field label="Ancoraggio">
          <GravitySelect
            value={ov.gravity}
            disabled={readOnly}
            onChange={(g) => onChange({ ...ov, gravity: g })}
          />
        </Field>
      </div>
      <Field label={ov.source_type === "binary" ? "Nome dello slot" : "Valore"}>
        <ExpressionField
          value={ov.source_value}
          onChange={(v) => onChange({ ...ov, source_value: v })}
          jsAmbientTypes={jsAmbientTypes}
          placeholder={ov.source_type === "binary" ? "logo" : "{{ $json.logo_url }}"}
        />
      </Field>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Scala">
          <NumberInput
            value={ov.scale}
            step={0.1}
            min={0.01}
            disabled={readOnly}
            onChange={(v) => onChange({ ...ov, scale: v ?? 1 })}
          />
        </Field>
        <Field label="Opacità (0–1)">
          <NumberInput
            value={ov.opacity}
            step={0.1}
            min={0}
            disabled={readOnly}
            onChange={(v) => onChange({ ...ov, opacity: v ?? 1 })}
          />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="Offset X">
          <NumberInput
            value={ov.offset_x}
            disabled={readOnly}
            onChange={(v) => onChange({ ...ov, offset_x: v ?? 0 })}
          />
        </Field>
        <Field label="Offset Y">
          <NumberInput
            value={ov.offset_y}
            disabled={readOnly}
            onChange={(v) => onChange({ ...ov, offset_y: v ?? 0 })}
          />
        </Field>
      </div>
    </div>
  );
}
